import React, { useMemo } from 'react'
import { useMutation, useQuery } from '@apollo/client'
import { useParams } from 'react-router-dom'
import { PageLoader } from 'components/PageLoader'
import { convertToCents, convertToDollars } from 'utils/priceFormatter'
import { ImageField } from 'components/DropzoneField'
import { PRODUCTS_PATH_S3, PUBLIC_PREFIX } from 'constants/images'
import { useImageUpload } from 'utils/saveImageS3'
import { FIND_PAUSE_TOKEN, UPDATE_PAUSE_TOKEN } from 'queries/pauseTokens'
import { findOnePauseToken, findOnePauseTokenVariables } from '__generatedTypes__/findOnePauseToken'
import { updatePauseToken, updatePauseTokenVariables } from '__generatedTypes__/updatePauseToken'
import { ProductStatus, UpdatePauseTokenInput } from '__generatedTypes__/globalTypes'
import { cmsRoutes, useLink } from 'routes/cmsRoutes'
import { toast } from 'utils/toast'
import { useFiles } from '../../../SuccessStory/Edit/hooks'
import { Form, FormValues, STATUSES } from './Form'

export const Edit = () => {
  const { id } = useParams()
  const { navigate } = useLink()
  const uploadImage = useImageUpload()
  const {
    fetchFromLinks,
    avatarFile: imageLightFile,
    coverFile: imageDarkFile,
    isLoading: isLoadingImages,
  } = useFiles()

  const { data, loading } = useQuery<findOnePauseToken, findOnePauseTokenVariables>(FIND_PAUSE_TOKEN, {
    variables: {
      pauseTokenId: String(id),
    },
    onCompleted: async (data) => {
      const pauseToken = data.findOnePauseToken

      await fetchFromLinks(
        {
          imageLink: pauseToken?.ligthModeImageUrl ?? '',
          initialLink: pauseToken?.ligthModeImageUrl ?? '',
        },
        {
          imageLink: pauseToken?.darkModeImageUrl ?? '',
          initialLink: pauseToken?.darkModeImageUrl ?? '',
        }
      )
    },
    onError: () => {
      navigate(cmsRoutes.PRODUCTS, undefined, {
        searchParams: new URLSearchParams({ tab: 'pause-tokens' }),
      })
    },
    fetchPolicy: 'network-only',
    skip: !id,
  })

  const [updatePauseTokenMutation] = useMutation<updatePauseToken, updatePauseTokenVariables>(UPDATE_PAUSE_TOKEN)

  const initialValues: FormValues | null = useMemo(() => {
    const pauseToken = data?.findOnePauseToken

    if (!pauseToken || isLoadingImages) {
      return null
    }

    return {
      name: pauseToken.name,
      description: pauseToken.description,
      count: pauseToken.amount,
      price: pauseToken.price ? convertToDollars(pauseToken.price) : 0,
      status: STATUSES.find(({ id }) => id === pauseToken.status),
      isPopular: pauseToken.isMostPopular,
      imageLight: imageLightFile && [imageLightFile],
      imageDark: imageDarkFile && [imageDarkFile],
    }
  }, [loading, isLoadingImages])

  const getFullImagePath = (path?: string) => {
    if (!path) {
      return undefined
    }

    return `${process.env.REACT_APP_IMAGEKIT_URL}/tr:h-default,w-default/${PUBLIC_PREFIX}/${path}`
  }

  const uploadFiles = async (imageLight: ImageField | undefined, imageDark: ImageField | undefined) => {
    const imageLightFile = imageLight?.isUploaded ? null : imageLight?.file
    const imageDarkFile = imageDark?.isUploaded ? null : imageDark?.file

    const [imageLightPath, imageDarkPath] = await Promise.all([
      imageLightFile
        ? uploadImage(imageLightFile, PRODUCTS_PATH_S3).then(getFullImagePath)
        : Promise.resolve(imageLight?.initialValue),
      imageDarkFile
        ? uploadImage(imageDarkFile, PRODUCTS_PATH_S3).then(getFullImagePath)
        : Promise.resolve(imageDark?.initialValue),
    ])

    return [imageLightPath, imageDarkPath]
  }

  const handleSubmit = async (values: FormValues) => {
    try {
      const id = data?.findOnePauseToken?.id
      const platformId = data?.findOnePauseToken?.platform.id

      if (!id || !platformId) {
        return
      }

      const [imageLight, imageDark] = await uploadFiles(values.imageLight?.[0], values.imageDark?.[0])

      const changedValues: UpdatePauseTokenInput = {
        name: values.name !== initialValues?.name ? values.name : undefined,
        description: values.description !== initialValues?.description ? values.description : undefined,
        amount: values.count !== initialValues?.count ? values.count : undefined,
        price: values.price !== initialValues?.price ? convertToCents(values.price ?? 0) : undefined,
        status:
          values.status?.id !== initialValues?.status?.id ? values.status?.id ?? ProductStatus.Inactive : undefined,
        isMostPopular: values.isPopular !== initialValues?.isPopular ? values.isPopular : undefined,
        ligthModeImageUrl: imageLight !== initialValues?.imageLight?.[0]?.file ? imageLight : undefined,
        darkModeImageUrl: imageDark !== initialValues?.imageDark?.[0]?.file ? imageDark : undefined,
      }
      const isChanged = Object.values(changedValues).filter((value) => value !== undefined).length

      if (isChanged) {
        await updatePauseTokenMutation({
          variables: {
            updatePauseTokenInput: {
              id,
              platform: {
                id: platformId,
              },
              ...changedValues,
            },
          },
        })

        toast.success('Pause token updated successfully')
      }

      navigate(cmsRoutes.PRODUCTS, undefined, {
        searchParams: new URLSearchParams({ tab: 'pause-tokens' }),
      })
    } catch (error) {
      toast.error('Failed to update pause token')
    }
  }

  if (loading || isLoadingImages) {
    return <PageLoader />
  }

  if (!initialValues) {
    return null
  }

  return <Form initialValues={initialValues} onSubmit={handleSubmit} />
}
